<script>
import UploadImages from '@/components/ux/upload/Upload-images.vue';
import bem from '@/utils/bem';
import constants from '@/pages/article/constants';

const b = bem('article-data-tab');

const { BLOG_PAGE_SIZE_VERSION_OPTIONS } = constants;

export default {
  components: { UploadImages },
  props: {
    formData: { type: Object, required: true },
    isPublished: { type: Boolean, default: false },
    isFeatured: { type: Boolean, default: false },
    desktopImage: { type: Array, default: () => [] },
    mobileImage: { type: Array, default: () => [] },
    previewImage: { type: Array, default: () => [] },
    currentArticle: { type: Object, default: () => ({}) },
    categoriesList: { type: Array, default: () => [] },
    validationErrors: { type: Object, default: () => ({}) },
    isNew: { type: Boolean, default: true },
  },
  emits: [
    'update:formData',
    'update:isPublished',
    'update:isFeatured',
    'update:desktopImage',
    'update:mobileImage',
    'update:previewImage',
  ],
  data() {
    return {
      BLOG_PAGE_SIZE_VERSION_OPTIONS,
      isMultiple: false,
    };
  },
  computed: {
    siteBaseUrl() {
      // Determine environment by checking the browser URL, not env vars
      if (window.location.hostname.includes('foreverstudios.com')) {
        return 'https://foreverstudios.com';
      }
      return 'http://3.220.231.76:3000';
    },
    canonicalUrl() {
      const slug = this.formData.titleSeo || '';
      if (!slug) return '';
      return `${this.siteBaseUrl}/${slug}/`;
    },
    articleDate() {
      if (!this.currentArticle?.addedAt) return '';
      return new Date(this.currentArticle.addedAt)
        .toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
    },
    updateDate() {
      if (!this.currentArticle?.updatedAt) return '';
      return new Date(this.currentArticle.updatedAt)
        .toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
    },
  },
  methods: {
    b(el, mods) { return b(el, mods); },

    updateField(field, value) {
      this.$emit('update:formData', {
        ...this.formData,
        [field]: value,
      });
    },

    sanitizeUrl(value) {
      const sanitized = value
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9-]/g, '')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
      this.updateField('titleSeo', sanitized);
    },

    hasError(field) {
      return !!this.validationErrors[field];
    },
  },
};
</script>

<template>
  <div :class="b()">
    <!-- Meta info bar -->
    <div
      v-if="!isNew && currentArticle?.id"
      :class="b('meta')"
    >
      <div :class="b('meta-item')">
        <span :class="b('meta-label')">Created:</span>
        <span :class="b('meta-value')">
          {{ articleDate }}
        </span>
      </div>
      <div :class="b('meta-item')">
        <span :class="b('meta-label')">Updated:</span>
        <span :class="b('meta-value')">
          {{ updateDate }}
        </span>
      </div>
      <div
        v-if="canonicalUrl"
        :class="b('meta-item', { url: true })"
      >
        <span :class="b('meta-label')">Live URL:</span>
        <a
          :href="canonicalUrl"
          target="_blank"
          :class="b('meta-link')"
        >{{ canonicalUrl }}</a>
      </div>
    </div>

    <!-- Grid: form left, images right -->
    <div :class="b('grid')">

      <!-- Left column: all form fields -->
      <div :class="b('form')">

        <!-- Article title -->
        <div :class="b('field', { error: hasError('cardTitle') })">
          <span :class="b('label')">
            Article Title
            <span :class="b('req')">*</span>
          </span>
          <a-input
            :value="formData.cardTitle"
            placeholder="Enter article title"
            @input="updateField('cardTitle', $event.target.value)"
          />
          <span
            v-if="hasError('cardTitle')"
            :class="b('error')"
          >This field is required</span>
        </div>

        <!-- SEO Title -->
        <div :class="b('field', { error: hasError('title') })">
          <span :class="b('label')">
            SEO Title
            <span :class="b('req')">*</span>
          </span>
          <a-input
            :value="formData.title"
            placeholder="Enter SEO title"
            @input="updateField('title', $event.target.value)"
          />
          <span
            v-if="hasError('title')"
            :class="b('error')"
          >This field is required</span>
        </div>

        <!-- SEO Description -->
        <div
          :class="b('field', { error: hasError('descriptionSeo') })"
        >
          <span :class="b('label')">
            SEO Description
            <span :class="b('req')">*</span>
          </span>
          <a-textarea
            :value="formData.descriptionSeo"
            placeholder="Enter SEO description"
            :auto-size="{ minRows: 3, maxRows: 5 }"
            @input="updateField(
              'descriptionSeo',
              $event.target.value
            )"
          />
          <span
            v-if="hasError('descriptionSeo')"
            :class="b('error')"
          >This field is required</span>
        </div>

        <!-- URL Slug with prefix -->
        <div :class="b('field', { error: hasError('titleSeo') })">
          <span :class="b('label')">
            URL Slug
            <span :class="b('req')">*</span>
          </span>
          <a-input
            :value="formData.titleSeo"
            placeholder="enter-url-slug"
            @input="sanitizeUrl($event.target.value)"
          >
            <template #addonBefore>
              /blog/
            </template>
          </a-input>
          <span
            v-if="hasError('titleSeo')"
            :class="b('error')"
          >This field is required</span>
        </div>

        <!-- Author + Category row -->
        <div :class="b('row-2col')">
          <div
            :class="b('field', { error: hasError('author') })"
          >
            <span :class="b('label')">
              Author
              <span :class="b('req')">*</span>
            </span>
            <a-input
              :value="formData.author"
              placeholder="Enter author name"
              @input="updateField('author', $event.target.value)"
            />
            <span
              v-if="hasError('author')"
              :class="b('error')"
            >This field is required</span>
          </div>

          <div
            :class="b('field', { error: hasError('categoryId') })"
          >
            <span :class="b('label')">
              Category
              <span :class="b('req')">*</span>
            </span>
            <a-select
              :value="formData.categoryId || undefined"
              placeholder="Select category"
              style="width: 100%"
              @change="updateField('categoryId', $event)"
            >
              <a-select-option
                v-for="cat in categoriesList"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.name }}
              </a-select-option>
            </a-select>
            <span
              v-if="hasError('categoryId')"
              :class="b('error')"
            >This field is required</span>
          </div>
        </div>

        <!-- Card Description — full width -->
        <div :class="b('field', { error: hasError('subTitle') })">
          <span :class="b('label')">
            Card Description
            <span :class="b('req')">*</span>
          </span>
          <a-input
            :value="formData.subTitle"
            placeholder="Enter card description"
            @input="updateField('subTitle', $event.target.value)"
          />
          <span
            v-if="hasError('subTitle')"
            :class="b('error')"
          >This field is required</span>
        </div>

        <!-- Page Size Version — hidden for now -->
        <div :class="b('field', { half: true, hidden: true })">
          <span :class="b('label')">Page Size Version</span>
          <a-select
            :value="formData.version"
            style="width: 100%"
            @change="updateField('version', $event)"
          >
            <a-select-option
              v-for="opt in BLOG_PAGE_SIZE_VERSION_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Toggles with separator -->
        <div :class="b('toggles')">
          <div :class="b('toggle')">
            <span :class="b('toggle-text')">Published</span>
            <a-switch
              :checked="isPublished"
              @change="$emit('update:isPublished', $event)"
            />
          </div>
          <div :class="b('toggle')">
            <span :class="b('toggle-text')">Featured</span>
            <a-switch
              :checked="isFeatured"
              @change="$emit('update:isFeatured', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Right column: image uploads -->
      <div :class="b('images')">
        <div
          :class="b('img-card', {
            main: true,
            error: hasError('desktopImage'),
          })"
        >
          <span :class="b('img-label')">
            Article image
            <span :class="b('req')">*</span>
          </span>
          <span :class="b('img-hint')">
            Recommended: 1544 × 596 px
          </span>
          <UploadImages
            :files="desktopImage"
            @update:files="$emit('update:desktopImage', $event)"
            :isMultiple="isMultiple"
            :initialImages="
              currentArticle?.imageUri
                ? [currentArticle.imageUri]
                : []
            "
          />
          <span
            v-if="hasError('desktopImage')"
            :class="b('error')"
          >Image is required</span>
        </div>

        <div
          :class="b('img-card', {
            error: hasError('mobileImage'),
          })"
        >
          <span :class="b('img-label')">
            Mobile image
            <span :class="b('req')">*</span>
          </span>
          <span :class="b('img-hint')">
            Recommended: 375 × 345 px
          </span>
          <UploadImages
            :files="mobileImage"
            @update:files="$emit('update:mobileImage', $event)"
            :isMultiple="isMultiple"
            :initialImages="
              currentArticle?.mobileImageUrl
                ? [currentArticle.mobileImageUrl]
                : []
            "
          />
          <span
            v-if="hasError('mobileImage')"
            :class="b('error')"
          >Image is required</span>
        </div>

        <div
          :class="b('img-card', {
            error: hasError('previewImage'),
          })"
        >
          <span :class="b('img-label')">
            Preview image
            <span :class="b('req')">*</span>
          </span>
          <span :class="b('img-hint')">
            Recommended: 477 × 368 px
          </span>
          <UploadImages
            :files="previewImage"
            @update:files="$emit('update:previewImage', $event)"
            :isMultiple="isMultiple"
            :initialImages="
              currentArticle?.previewImageUri
                ? [currentArticle.previewImageUri]
                : []
            "
          />
          <span
            v-if="hasError('previewImage')"
            :class="b('error')"
          >Image is required</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/media';

.article-data-tab {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;

  // --- Meta info bar ---
  &__meta {
    display: flex;
    align-items: center;
    gap: 32px;
    padding: 14px 24px;
    margin-bottom: 32px;
    background: var(--Background-Secondary, #f7f8fa);
    border: 1px solid var(--Border-Color, #e5e7eb);
    border-radius: 12px;
    box-shadow: var(--Shadow-Small, 0 1px 2px rgba(0, 0, 0, 0.05));
  }

  &__meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;

    &_url {
      min-width: 0; // allow truncation
      overflow: hidden;
    }
  }

  &__meta-label {
    font-weight: 600;
    color: var(--Text-Secondary, #64748b);
    white-space: nowrap;
  }

  &__meta-value {
    color: var(--Text-Primary, #0f172a);
    white-space: nowrap;
  }

  &__meta-link {
    color: #4f72e8;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      text-decoration: underline;
    }
  }

  // --- Main grid ---
  &__grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 40px;
    align-items: start;
  }

  // --- Form column ---
  &__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  // --- Field ---
  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &_half {
      max-width: 50%;
    }

    &_hidden {
      display: none;
    }

    &_error {
      :deep(.ant-input),
      :deep(.ant-input-affix-wrapper),
      :deep(.ant-select-selector),
      :deep(.ant-input-group .ant-input),
      :deep(.ant-input-group-addon) {
        border-color: var(--Error, #e63946) !important;
      }
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    color: var(--Text-Primary, #0f172a);
  }

  &__req {
    color: #e63946;
  }

  &__error {
    font-size: 12px;
    color: #e63946;
    margin-top: 2px;
  }

  // --- 2-column row (Author + Category) ---
  &__row-2col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  // --- Toggles ---
  &__toggles {
    display: flex;
    align-items: center;
    gap: 32px;
    padding-top: 16px;
    margin-top: 8px;
    border-top: 1px solid var(--Border-Color, #e5e7eb);
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__toggle-text {
    font-size: 14px;
    font-weight: 500;
    color: var(--Text-Primary, #0f172a);
    user-select: none;
  }

  // --- Images column ---
  &__images {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-bottom: 48px;
  }

  &__img-card {
    display: flex;
    flex-direction: column;
    gap: 8px;

    // Main (article) image — larger, 4:3 aspect
    &_main {
      :deep(.ant-upload-list-picture-card) {
        .ant-upload-list-item-container,
        .ant-upload.ant-upload-select {
          width: 100% !important;
          height: auto !important;
          aspect-ratio: 4 / 3;
        }
      }
    }

    // Secondary images — square, 192px
    &:not(.article-data-tab__img-card_main) {
      :deep(.ant-upload-list-picture-card) {
        .ant-upload-list-item-container,
        .ant-upload.ant-upload-select {
          width: 192px !important;
          height: 192px !important;
        }
      }
    }

    &_error {
      :deep(.ant-upload) {
        border-color: var(--Error, #e63946) !important;
      }
    }
  }

  &__img-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--Text-Primary, #0f172a);
  }

  &__img-hint {
    font-size: 12px;
    font-weight: 500;
    color: #f0a882;
    letter-spacing: 0.02em;
    margin-top: -4px;
  }

  // --- Ant Design overrides ---
  :deep(.ant-input),
  :deep(.ant-input-affix-wrapper) {
    border-radius: 8px;
    font-size: 14px;
    padding: 8px 12px;
    transition: all 0.2s ease;
  }

  :deep(.ant-input:focus),
  :deep(.ant-input-focused),
  :deep(.ant-input-affix-wrapper-focused) {
    border-color: var(--Primary, #5b8fa3);
    box-shadow: 0 0 0 1px var(--Primary, #5b8fa3);
  }

  :deep(textarea.ant-input) {
    padding: 10px 12px;
  }

  :deep(.ant-select .ant-select-selector) {
    border-radius: 8px !important;
    height: 40px !important;
    padding: 4px 12px !important;
    font-size: 14px;
  }

  :deep(.ant-input-group-addon) {
    border-radius: 8px 0 0 8px;
    background: var(--Background-Tertiary, #edf0f2);
    color: var(--Text-Secondary, #64748b);
    font-size: 14px;
    padding: 0 12px;
  }

  :deep(.ant-input-group .ant-input) {
    border-radius: 0 8px 8px 0;
  }

  // Upload cards: rounded corners, overflow hidden
  :deep(.ant-upload-list-picture-card) {
    .ant-upload-list-item-container {
      border-radius: 12px;
      overflow: hidden;
    }

    .ant-upload.ant-upload-select {
      border-radius: 12px;
      border: 2px dashed var(--Border-Color, #e5e7eb);
      transition: border-color 0.2s;

      &:hover {
        border-color: var(--Primary, #5b8fa3);
      }
    }
  }

  // --- Responsive ---
  @include media($small) {
    padding: 20px 16px;

    .article-data-tab__grid {
      grid-template-columns: 1fr;
      gap: 32px;
    }

    .article-data-tab__row-2col {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .article-data-tab__field_half {
      max-width: 100%;
    }

    .article-data-tab__meta {
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
